import React, { useState } from 'react';
import { api } from '../data/api';
import { ArrowLeft, MagnifyingGlass, Check, Warning } from '@phosphor-icons/react';

export default function Login({ onComplete, onSwitchToRegister }) {
  const [username, setUsername] = useState('');
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRecover, setShowRecover] = useState(false);
  const [recoverUsername, setRecoverUsername] = useState('');
  const [recoverResult, setRecoverResult] = useState(null); // 'found' | 'not_found' | null
  const [recoverLoading, setRecoverLoading] = useState(false);

  const handleDemo = async () => {
    setError('');
    setLoading(true);
    try {
      // Seed demo if needed, then login
      await api.seedDemo().catch(() => {});
      const res = await api.login({ username: 'demo', passcode: 'demo1234' });
      localStorage.setItem('leprotocol_token', res.token);
      localStorage.setItem('leprotocol_user', res.user.user_id);
      onComplete(res.user);
    } catch (err) {
      setError('Demo login failed — try again');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.login({ username, passcode });
      localStorage.setItem('leprotocol_token', res.token);
      localStorage.setItem('leprotocol_user', res.user.user_id);
      onComplete(res.user);
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRecover = async () => {
    if (!recoverUsername.trim() || recoverUsername.trim().length < 3) return;
    setRecoverLoading(true);
    setRecoverResult(null);
    try {
      await api.checkUsername(recoverUsername.trim());
      setRecoverResult('found');
    } catch {
      setRecoverResult('not_found');
    } finally {
      setRecoverLoading(false);
    }
  };

  if (showRecover) {
    return (
      <div className="min-h-screen bg-base flex items-start justify-center p-4 pt-12 overflow-y-auto" data-testid="recover-screen">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <h1 className="font-head text-4xl sm:text-5xl font-black uppercase tracking-tighter text-white">LE PROTOCOL</h1>
            <p className="text-accent font-head text-base sm:text-lg font-bold uppercase tracking-wide mt-1">Recover Account</p>
          </div>

          <div className="glass rounded-xl p-4 sm:p-6 space-y-3">
            <p className="text-xs text-neutral-400 leading-relaxed">
              Enter your username to check if your account exists. If found, use your username and passcode to log in.
            </p>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1 block">Username</label>
              <input type="text" value={recoverUsername}
                onChange={(e) => { setRecoverUsername(e.target.value); setRecoverResult(null); }}
                className="w-full bg-black/50 border border-border text-white h-12 sm:h-14 text-base rounded-md px-4 focus:border-accent focus:ring-1 focus:ring-accent outline-none"
                placeholder="Enter your username" data-testid="recover-username-input" />
            </div>

            {recoverResult === 'found' && (
              <div className="bg-success/10 border border-success/30 rounded-md p-3 flex items-center gap-2" data-testid="recover-found">
                <Check size={18} weight="bold" className="text-success flex-shrink-0" />
                <div>
                  <div className="text-sm text-success font-bold">Account found!</div>
                  <div className="text-xs text-neutral-400 mt-0.5">Use your username and passcode to log in.</div>
                </div>
              </div>
            )}

            {recoverResult === 'not_found' && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-md p-3 flex items-center gap-2" data-testid="recover-not-found">
                <Warning size={18} weight="bold" className="text-red-400 flex-shrink-0" />
                <div>
                  <div className="text-sm text-red-400 font-bold">Account not found</div>
                  <div className="text-xs text-neutral-400 mt-0.5">Check the spelling or create a new account.</div>
                </div>
              </div>
            )}

            <button onClick={handleRecover} disabled={recoverLoading || recoverUsername.trim().length < 3}
              className={`w-full h-12 sm:h-14 rounded-md font-head font-bold text-base uppercase tracking-wide flex items-center justify-center gap-2 transition-all active:scale-95 ${
                recoverUsername.trim().length >= 3 ? 'bg-accent text-black' : 'bg-elevated text-neutral-600 border border-border cursor-not-allowed'
              }`} data-testid="recover-check-btn">
              <MagnifyingGlass size={18} weight="bold" />
              {recoverLoading ? 'Checking...' : 'Check Account'}
            </button>

            {recoverResult === 'found' && (
              <button onClick={() => { setShowRecover(false); setUsername(recoverUsername); }}
                className="w-full h-12 rounded-md bg-brand text-white font-head font-bold text-sm uppercase active:scale-95 transition-all"
                data-testid="recover-go-login">
                Go to Login
              </button>
            )}

            <button onClick={() => { setShowRecover(false); setRecoverResult(null); }}
              className="w-full h-9 flex items-center justify-center gap-1 text-neutral-500 text-xs font-bold uppercase active:scale-95"
              data-testid="recover-back-btn">
              <ArrowLeft size={12} weight="bold" /> Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base flex items-start justify-center p-4 pt-12 overflow-y-auto" data-testid="login-screen">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="font-head text-4xl sm:text-5xl font-black uppercase tracking-tighter text-white">LE PROTOCOL</h1>
          <p className="text-brand font-head text-base sm:text-lg font-bold uppercase tracking-wide mt-1">Welcome Back</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-md mb-3 text-center" data-testid="login-error">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="glass rounded-xl p-4 sm:p-6 space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1 block">Username</label>
            <input type="text" required value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full bg-black/50 border border-border text-white h-12 sm:h-14 text-base rounded-md px-4 focus:border-brand focus:ring-1 focus:ring-brand outline-none"
              placeholder="Enter username" data-testid="login-username-input" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1 block">Passcode</label>
            <input type="password" required value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              className="w-full bg-black/50 border border-border text-white h-12 sm:h-14 text-base rounded-md px-4 focus:border-brand focus:ring-1 focus:ring-brand outline-none"
              placeholder="Enter passcode" data-testid="login-passcode-input" />
          </div>
          <button type="submit" disabled={loading}
            className="w-full bg-brand text-white font-head font-bold text-base sm:text-lg uppercase tracking-wide h-12 sm:h-14 rounded-md hover:bg-brand-hover transition-colors active:scale-95"
            data-testid="login-submit-btn">
            {loading ? 'LOGGING IN...' : 'LOCK IN'}
          </button>

          <button type="button" onClick={() => setShowRecover(true)}
            className="w-full text-center text-accent text-xs font-bold py-1.5 hover:text-white transition-colors"
            data-testid="recover-link">
            Forgot your username? <span className="underline">Recover Account</span>
          </button>

          <div className="relative flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[10px] text-neutral-600 uppercase">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <button type="button" onClick={handleDemo} disabled={loading}
            className="w-full h-11 rounded-lg glass-elevated text-white font-head font-bold text-sm uppercase tracking-wide flex items-center justify-center gap-2 active:scale-95 transition-all glow-subtle"
            data-testid="demo-login-btn">
            {loading ? 'Loading...' : 'Try Demo'}
          </button>

          <button type="button" onClick={onSwitchToRegister}
            className="w-full text-center text-neutral-500 text-sm py-1 hover:text-white transition-colors"
            data-testid="switch-to-register-btn">
            New here? <span className="text-brand font-bold">CREATE ACCOUNT</span>
          </button>
        </form>
      </div>
    </div>
  );
}
